import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import "./PreviewSpacePage.css";
import { getSessionParams } from "../session/utils/sessionStorage";
import { handleRedirectBack } from "../../shared/utils/navigationUtils";
import Spinner from "react-bootstrap/Spinner";
import { rejectSession } from "../session/utils/rejectSession";
import SessionCountdown from "../session/components/SessionCountdown";
import { apiFetch } from "../../shared/utils/http";
import { logger } from "../../shared/utils/logger";
import { useTranslation } from "react-i18next";

const logPrefix = "[PreviewSpace]";

const EvidenceSubmission: React.FC = () => {
  const [showPdf, setShowPdf] = useState<boolean>(false);
  const [pdfExists, setPdfExists] = useState<boolean>(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfName, setPdfName] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [retryCount, setRetryCount] = useState<number>(0);
  const [redirecting, setRedirecting] = useState<boolean>(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!hasInteracted) {
      void handlePreview();
    }
  }, [hasInteracted]);

  useEffect(() => {
    if (showPdf) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => document.body.classList.remove("modal-open");
  }, [showPdf]);

  const readJsonSafe = async (resp: Response) => {
    const ct = (resp.headers.get("content-type") || "").toLowerCase();
    if (!ct.includes("application/json")) {
      logger.warn(`${logPrefix} Expected JSON but got content-type="${ct}" (possible SPA fallback)`);
      return null;
    }
    if (resp.status === 204) return null;
    try {
      return await resp.json();
    } catch (e) {
      logger.error(`${logPrefix} JSON parse failed:`, e);
      return null;
    }
  };

  const handlePreview = async () => {
    if (redirecting) {
      logger.info(`${logPrefix} Abort preview — already redirecting.`);
      return;
    }

    const storedParams = getSessionParams();
    if (!storedParams?.sessionId) {
      logger.warn(`${logPrefix} Session ID missing — redirecting to error.`);
      navigate("/error", {
        state: { errorCode: "400", errorMessage: t("preview.error.sessionIdMissing") },
      });
      return;
    }

    const maxRetries = 10;
    let attempt = 0;

    logger.info(`${logPrefix} Starting PDF fetch…`);
    setLoading(true);
    setRetryCount(0);
    setPdfUrl(null);
    setPdfName(null);
    setShowPdf(false);
    setPdfExists(false);

    const query = new URLSearchParams({ session: storedParams.sessionId }).toString();

    while (attempt < maxRetries) {
      const tryNo = attempt + 1;
      logger.info(`${logPrefix} Attempt ${tryNo}/${maxRetries}`);
      try {
        setRetryCount(tryNo);

        const resp = await apiFetch(`/proxy/getPdf?${query}`, {
          method: "GET",
          headers: { Accept: "application/pdf" },
          credentials: "include",
        });

        const status = resp.status;
        const ct = resp.headers.get("content-type") || "";
        logger.info(`${logPrefix} GET /proxy/getPdf ->`, { status, ct });

        if (status === 200) {
          const blob = await resp.blob();

          if (!blob || blob.size === 0) {
            logger.warn(`${logPrefix} Empty blob — redirecting to error.`);
            setLoading(false);
            navigate("/error", {
              state: {
                errorCode: "500",
                errorMessage: t("preview.error.documentNotRetrieved"),
              },
            });
            return;
          }

          const textSample = await blob.text();
          const knownFallback = '{"code":"BAAS4","message":"No customer found"}';
          if (textSample.trim() === knownFallback || textSample.startsWith("<!DOCTYPE")) {
            logger.warn(`${logPrefix} Fallback content detected in PDF response — redirecting to error.`);
            setLoading(false);
            navigate("/error", {
              state: {
                errorCode: "500",
                errorMessage: t("preview.error.documentNotRetrieved"),
              },
            });
            return;
          }

          const finalBlob =
              blob.type === "application/pdf" ? blob : new Blob([textSample], { type: "application/pdf" });

          const cd = resp.headers.get("Content-Disposition");
          const match = cd?.match(/filename="(.+?)"/);
          const fileName = match?.[1] || `preview-document-${storedParams.sessionId}.pdf`;

          const objectUrl = URL.createObjectURL(finalBlob);
          logger.info(`${logPrefix} PDF ready: ${fileName}`);

          setPdfName(fileName);
          setPdfUrl(objectUrl);
          setPdfExists(true);
          setShowPdf(true);
          setLoading(false);
          logger.info(`${logPrefix} Preview modal opened.`);
          return;
        }

          if (status === 202 || status === 503 || status === 504) {
              logger.info(`${logPrefix} PDF not ready yet [${status}], retrying...`);
              throw new Error(`Retryable error: ${status}`);
          }

          if (status === 404) {
              const body = await readJsonSafe(resp);
              logger.warn(`${logPrefix} Evidence definitively not found`, body);

              setLoading(false);
              navigate("/error", {
                  state: {
                      errorCode: "404",
                      errorMessage: t("preview.error.documentNotRetrieved"),
                  },
              });
              return;
          }

          if (status === 401) {
              logger.warn(`${logPrefix} Unauthorized — redirecting to error.`);
              setLoading(false);
              navigate("/error", {
                  state: {
                      errorCode: "401",
                      errorMessage: t("preview.error.unauthorizedAccess"),
                  },
              });
              return;
          }
      } catch (e) {
        logger.error(`${logPrefix} Attempt ${attempt + 1} failed:`, e);
      }

      attempt++;
      if (attempt < maxRetries) {
        logger.info(`${logPrefix} Waiting 5s before next retry…`);
        await new Promise((r) => setTimeout(r, 5000));
      }
    }

    logger.error(`${logPrefix} All retries failed — redirecting to error page.`);
    setLoading(false);
    setPdfExists(false);
    setShowPdf(false);
    setPdfUrl(null);
    setPdfName(null);

    navigate("/error", {
      state: {
        errorCode: "500",
        errorMessage: t("preview.error.documentNotRetrieved"),
      },
    });
  };

  const handleAccept = async () => {
    const sp = getSessionParams();
    if (!sp?.sessionId) {
      navigate("/error", {
        state: { errorCode: "400", errorMessage: t("preview.error.sessionIdMissing") },
      });
      return;
    }

    setShowPdf(false);
    setHasInteracted(true);

    await withRedirect(async () => {
      const query = new URLSearchParams({ session: sp.sessionId! }).toString();
      try {
        logger.info(`${logPrefix} POST /proxy/acceptPreview`);
        const resp = await apiFetch(`/proxy/acceptPreview?${query}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });

        logger.info(`${logPrefix} /proxy/acceptPreview ->`, {
          status: resp.status,
          statusText: resp.statusText,
        });

        if (resp.ok) {
          setRedirecting(true);
          await handleRedirectBack(navigate);
        } else {
          const text = await readJsonSafe(resp);
          logger.error(`${logPrefix} Accept failed`, resp.status, text);
          navigate("/error", {
            state: {
              errorCode: String(resp.status),
              errorMessage: t("preview.error.acceptFailed"),
            },
          });
        }
      } catch (e) {
        logger.error(`${logPrefix} Accept error:`, e);
        navigate("/error", {
          state: { errorCode: "500", errorMessage: t("preview.error.acceptRequestFailed") },
        });
      }
    });
  };

  const handleReject = async () => {
    setShowPdf(false);
    setHasInteracted(true);

    await withRedirect(async () => {
      try {
        await rejectSession(navigate);
      } catch (err) {
        logger.warn(`${logPrefix} rejectSession failed:`, err);
      }

      try {
        setRedirecting(true);
        await handleRedirectBack(navigate, true);
      } catch (redirectErr) {
        logger.error(`${logPrefix} handleRedirectBack failed:`, redirectErr);
        setRedirecting(false);
      }
    });
  };

  const withRedirect = async (action: () => Promise<void>) => {
    setRedirecting(true);
    await action();
  };

  const spinnerBase: React.CSSProperties = {
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "4px solid #e5e7eb",
    borderTop: "4px solid #1976d2",
    animation: "spin 0.9s linear infinite",
    display: "inline-block",
  };

  const spinnerSmall: React.CSSProperties = {
    width: 18,
    height: 18,
    border: "3px solid #e5e7eb",
    borderTop: "3px solid #1976d2",
  };

  const SpinnerInline = ({
                           id,
                           small,
                           "aria-label": ariaLabel = t("preview.loading.default"),
                         }: {
    id?: string;
    small?: boolean;
    "aria-label"?: string;
  }) => (
      <span
          id={id}
          role="status"
          aria-live="polite"
          aria-label={ariaLabel}
          style={small ? { ...spinnerBase, ...spinnerSmall } : spinnerBase}
      />
  );

  return (
      <div id="BOX_EVIDENCESUBMISSION">
        <SessionCountdown />
        <main>
          <section className="content">
            <div>
              <h1 id="TXT_EVIDENCEHEADING">
                {t("preview.heading")}
              </h1>

              {loading ? (
                  <div className="loading-container" id="SPINNER_FETCH" aria-live="polite">
                    <SpinnerInline id="SPINNER_FETCH_ICON" />
                    <p id="TXT_FETCHMESSAGE">{t("preview.loading.fetchingDocument")}</p>
                  </div>
              ) : (
                  <div>
                    <div className="evidence-section">
                      <h2 id="TXT_EVIDENCESECTIONHEADER">{t("preview.sectionHeading")}</h2>
                      <table id="TABLE_EVIDENCE">
                        <thead>
                        <tr>
                          <th>{t("preview.table.evidence")}</th>
                          <th>{t("preview.table.language")}</th>
                          <th className="evidence">{t("preview.table.actions")}</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr id="ROW_EVIDENCE_0">
                          <td id="CELL_PDFNAME">{pdfName ? pdfName : t("preview.loading.fileName")}</td>
                          <td>{t("preview.table.languageEnglish")}</td>
                          <td>
                            <button
                                id="BTN_PREVIEWPDF"
                                className="preview-btn"
                                onClick={handlePreview}
                                disabled={!pdfUrl || loading}
                            >
                              {loading ? (
                                  <Spinner animation="border" size="sm" id="SPINNER_PREVIEW" />
                              ) : (
                                  t("preview.button.preview")
                              )}
                            </button>

                            {showPdf && pdfUrl &&
                                createPortal(
                                    (
                                        <div className="modal" id="MODAL_PDFPREVIEW">
                                          <div className="modal-content">
                                    <span
                                        className="close"
                                        id="BTN_CLOSEPDFMODAL"
                                        onClick={() => setShowPdf(false)}
                                    >
                                      &times;
                                    </span>
                                            <iframe
                                                id="FRAME_PDFPREVIEW"
                                                title={t("preview.modal.pdfTitle")}
                                                src={pdfUrl}
                                                width="100%"
                                                height="100%"
                                                style={{ border: "none" }}
                                            />
                                            {pdfExists && (
                                                <div className="modal-actions" id="BOX_MODALACTIONS">
                                                  <button
                                                      id="BTN_REJECTPDF"
                                                      className="rejectButton"
                                                      onClick={handleReject}
                                                      disabled={redirecting}
                                                  >
                                                    {redirecting ? (
                                                        <Spinner animation="border" size="sm" id="SPINNER_REJECT" />
                                                    ) : (
                                                        t("preview.button.reject")
                                                    )}
                                                  </button>
                                                  <button
                                                      id="BTN_ACCEPTPDF"
                                                      className="acceptButton"
                                                      onClick={handleAccept}
                                                      disabled={redirecting}
                                                  >
                                                    {redirecting ? (
                                                        <Spinner animation="border" size="sm" id="SPINNER_ACCEPT" />
                                                    ) : (
                                                        t("preview.button.accept")
                                                    )}
                                                  </button>
                                                </div>
                                            )}
                                          </div>
                                        </div>
                                    ),
                                    document.body
                                )}
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
              )}
            </div>
          </section>

          {!loading && !redirecting && (
              <div className="button-container" id="BOX_BACKBUTTON">
                <button
                    id="BTN_CANCEL"
                    className="cancelButton"
                    onClick={handleReject}
                    disabled={redirecting}
                >
                  {redirecting ? (
                      <Spinner animation="border" size="sm" id="SPINNER_BACKTOPORTAL" />
                  ) : (
                      t("preview.button.backToPortal")
                  )}
                </button>
              </div>
          )}
        </main>

        {redirecting && (
            <div className="redirect-overlay">
              <div className="redirect-modal" id="MODAL_REDIRECT">
                <Spinner animation="border" role="status" id="SPINNER_REDIRECT" />
                <p id="TXT_REDIRECTMESSAGE">{t("preview.loading.redirectingRequesterPortal")}</p>
              </div>
            </div>
        )}
      </div>
  );
};

export default EvidenceSubmission;
